import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { SEND_MESSAGE_V2 } from '../../../graphql/operations';
import DateRangeInput from './DateRangeInput';
import './widgets.css';

const SUBMIT_ACTION_TO_COMMAND = {
  'order.select_type': '/select-order-type',
  'order.select_status': '/select-order-status'
};

function normalize(value) {
  return String(value == null ? '' : value).trim();
}

function toBase64Utf8(value) {
  if (typeof TextEncoder !== 'undefined') {
    const encoded = new TextEncoder().encode(value);
    let binary = '';
    encoded.forEach((charCode) => {
      binary += String.fromCharCode(charCode);
    });
    return window.btoa(binary);
  }
  const encoded = encodeURIComponent(value).replace(
    /%([0-9A-F]{2})/g,
    (_match, hexByte) => String.fromCharCode(parseInt(hexByte, 16))
  );
  return window.btoa(encoded);
}

function normalizeOptions(options) {
  const normalized = [];
  const seenValues = new Set();
  (options || []).forEach((option) => {
    if (option && typeof option === 'object') {
      const label = normalize(option.label);
      const value = normalize(option.value);
      if (!label || !value) return;
      const key = value.toLowerCase();
      if (seenValues.has(key)) return;
      seenValues.add(key);
      normalized.push({ label, value });
      return;
    }
    const raw = normalize(option);
    if (!raw) return;
    const key = raw.toLowerCase();
    if (seenValues.has(key)) return;
    seenValues.add(key);
    normalized.push({ label: raw, value: raw });
  });
  return normalized.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
}

function filterOptions(options, inputValue) {
  const query = normalize(inputValue).toLowerCase();
  if (!query) return options;
  return options.filter((option) => (
    normalize(option.label).toLowerCase().includes(query)
    || normalize(option.value).toLowerCase().includes(query)
  ));
}

function findMatchingOption(rawValue, options) {
  const value = normalize(rawValue).toLowerCase();
  if (!value) return null;
  return options.find(
    (option) => (
      normalize(option.value).toLowerCase() === value
      || normalize(option.label).toLowerCase() === value
    )
  ) || null;
}

/**
 * OrderPickerCard — renders a single `search-select` picker used when a user's
 * supplied order type or status does not match the catalog exactly.
 *
 * Handles widgetTypes:
 *   order_type_picker   → submit action `order.select_type`
 *   order_status_picker → submit action `order.select_status`
 *
 * Data shape:
 * {
 *   status: { text: "Select order type" },
 *   children: [Title, Text, Caption, Divider, Spacer, Badge] (optional, passthrough),
 *   picker: {
 *     fieldName: "type" | "status",
 *     label: "Order type",
 *     options: [{ label, value }] (server pre-sorts; we re-sort defensively),
 *     pendingIntent: { ... } (opaque, echoed on submit)
 *   },
 *   submit: {
 *     label: "Continue",
 *     action: { type: "order.select_type" | "order.select_status", payload: { pendingActionId? } }
 *   },
 *   cancel: {
 *     label: "Cancel",
 *     action: { type: "record.discard_action", payload: { pendingActionId } }
 *   }
 * }
 */
function OrderPickerCard({ data, widgetType, conversationId }) {
  const [sendMessage] = useMutation(SEND_MESSAGE_V2);
  const isTypePicker = widgetType === 'order_type_picker';
  const picker = useMemo(() => (data && data.picker) || {}, [data]);
  const submit = (data && data.submit) || {};
  const cancel = (data && data.cancel) || {};

  const children = useMemo(() => (data && data.children) || [], [data]);
  const options = useMemo(() => {
    const fallbackOptions = widgetType === 'order_status_picker'
      ? data?.statusOptions
      : data?.typeOptions;
    return normalizeOptions(picker.options || fallbackOptions || []);
  }, [picker.options, widgetType, data?.statusOptions, data?.typeOptions]);

  const fallbackFieldName = widgetType === 'order_status_picker' ? 'status' : 'type';
  const fieldName = normalize(picker.fieldName) || fallbackFieldName;
  const fieldLabel = normalize(picker.label)
    || (fieldName === 'status' ? 'Order status' : 'Order type');

  const submitActionType = normalize(submit.action?.type)
    || (fieldName === 'status' ? 'order.select_status' : 'order.select_type');
  const cancelActionType = normalize(cancel.action?.type) || 'record.discard_action';

  const pendingActionId = normalize(
    submit.action?.payload?.pendingActionId
    || cancel.action?.payload?.pendingActionId
    || data?.pendingActionId
  );
  const safePendingActionId = pendingActionId.replace(/[^a-zA-Z0-9_-]/g, '-');
  const pendingIntent = (picker.pendingIntent && typeof picker.pendingIntent === 'object')
    ? picker.pendingIntent
    : (data?.pendingIntent && typeof data.pendingIntent === 'object'
      ? data.pendingIntent
      : null);
  const pendingIntentFromAction = (submit.action?.payload?.pendingIntent && typeof submit.action.payload.pendingIntent === 'object'
      ? submit.action.payload.pendingIntent
      : null);
  const resolvedPendingIntent = pendingIntent || pendingIntentFromAction;

  const [value, setValue] = useState('');
  const [displayValue, setDisplayValue] = useState('');
  const [submitState, setSubmitState] = useState('idle');
  const [cancelState, setCancelState] = useState('idle');
  const [menuOpen, setMenuOpen] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const cardRef = useRef(null);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (cardRef.current && !cardRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, []);

  useEffect(() => {
    const draftFrom = normalize(picker.dateDraft?.fromDate || data?.dateDraft?.fromDate);
    const draftTo = normalize(picker.dateDraft?.toDate || data?.dateDraft?.toDate);
    setFromDate(draftFrom);
    setToDate(draftTo);
  }, [picker.dateDraft, data?.dateDraft]);

  const filteredOptions = useMemo(
    () => filterOptions(options, displayValue),
    [options, displayValue]
  );

  const selectedOption = useMemo(
    () => findMatchingOption(value, options),
    [value, options]
  );

  const headerText = data?.status?.text || normalize(data?.title) || (fieldName === 'status' ? 'Select status' : 'Select type');
  const headerClass = 'widget-header-warning';
  const emptyCopy = fieldName === 'status'
    ? 'No statuses available for this type yet.'
    : 'No order types available yet.';

  const isSubmitDisabled = (
    !conversationId
    || !pendingActionId
    || !selectedOption
    || submitState === 'loading'
    || submitState === 'sent'
  );

  const handleSelect = (option) => {
    setValue(option.value);
    setDisplayValue(option.label);
    setMenuOpen(false);
  };

  const handleInput = (event) => {
    const next = event.target.value;
    setDisplayValue(next);
    const match = findMatchingOption(next, options);
    setValue(match ? match.value : '');
    setMenuOpen(true);
  };

  const onSubmit = async () => {
    if (isSubmitDisabled) return;
    setSubmitState('loading');
    const commandPrefix = SUBMIT_ACTION_TO_COMMAND[submitActionType] || '/select-order-type';
    const payloadB64 = toBase64Utf8(JSON.stringify({
      value: selectedOption.value,
      label: selectedOption.label,
      pendingIntent: resolvedPendingIntent || null,
      ...(isTypePicker ? { fromDate, toDate } : {})
    }));
    const idSegment = pendingActionId;
    const content = `${commandPrefix} ${idSegment} ${payloadB64}`;

    try {
      await sendMessage({
        variables: {
          conversationId,
          content
        }
      });
      setSubmitState('sent');
    } catch (error) {
      console.error('Failed to submit order picker:', error);
      setSubmitState('idle');
    }
  };

  const onCancel = async () => {
    if (!conversationId || cancelState === 'loading' || cancelState === 'sent') return;
    const idSegment = pendingActionId || '';
    if (cancelActionType === 'record.discard_action' && !idSegment) return;
    setCancelState('loading');
    try {
      await sendMessage({
        variables: {
          conversationId,
          content: `/discard ${idSegment}`.trim()
        }
      });
      setCancelState('sent');
    } catch (error) {
      console.error('Failed to cancel order picker:', error);
      setCancelState('idle');
    }
  };

  return (
    <div className="widget-card widget-card-form" ref={cardRef} data-widget-type={widgetType}>
      <div className={`widget-card-status-header ${headerClass}`}>
        <span className="widget-status-dot" />
        <span className="widget-status-text">{headerText}</span>
      </div>

      <div className="widget-card-content">
        {children.map((child, index) => {
          switch (child.type) {
            case 'Title':
              return <h4 key={index} className="widget-card-title">{child.value}</h4>;
            case 'Badge':
              return (
                <span key={index} className={`widget-badge widget-badge-${child.color || 'primary'}`}>
                  {child.label}
                </span>
              );
            case 'Text':
              return (
                <p key={index} className={`widget-text ${child.weight === 'semibold' ? 'widget-text-bold' : ''}`}>
                  {child.value}
                </p>
              );
            case 'Caption':
              return <p key={index} className="widget-caption">{child.value}</p>;
            case 'Divider':
              return <hr key={index} className="widget-divider" />;
            case 'Spacer':
              return <div key={index} className="widget-spacer" />;
            default:
              return null;
          }
        })}

        <div className="widget-form-grid widget-form-grid-picker">
          <label className="widget-form-field widget-form-field-full">
            <span className="widget-form-label">{fieldLabel}</span>
            {options.length === 0 ? (
              <span className="widget-caption" role="status">{emptyCopy}</span>
            ) : (
              <div className="widget-search-select">
                <div className="widget-search-select-input-wrap">
                  <input
                    type="text"
                    value={displayValue}
                    onFocus={() => setMenuOpen(true)}
                    onChange={handleInput}
                    onKeyDown={(event) => {
                      if (event.key === 'Escape') setMenuOpen(false);
                      if (event.key === 'ArrowDown') setMenuOpen(true);
                    }}
                    className="widget-form-input widget-search-select-input"
                    aria-label={fieldLabel}
                  />
                  <span className="widget-search-select-caret" aria-hidden="true">v</span>
                </div>
                {menuOpen && filteredOptions.length > 0 && (
                  <div
                    className={`widget-search-select-menu ${isTypePicker ? 'widget-search-select-menu-inline' : ''}`}
                    role="listbox"
                    aria-label={`${fieldLabel} options`}
                  >
                    {filteredOptions.map((option) => (
                      <button
                        key={`${fieldName}-${option.value}`}
                        type="button"
                        className="widget-search-select-option"
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => handleSelect(option)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </label>
          {isTypePicker && (
            <label className="widget-form-field widget-form-field-full">
              <span className="widget-form-label">Date range (optional)</span>
              <DateRangeInput
                id={`order-type-picker-date-${safePendingActionId || 'pending'}`}
                fromDate={fromDate}
                toDate={toDate}
                onChange={({ fromDate: nextFromDate, toDate: nextToDate }) => {
                  setFromDate(normalize(nextFromDate));
                  setToDate(normalize(nextToDate));
                }}
              />
            </label>
          )}
        </div>

        <div className="widget-card-actions">
          <button
            type="button"
            className={`widget-btn widget-btn-confirm ${submitState === 'loading' ? 'is-loading' : ''} ${submitState === 'sent' ? 'is-done' : ''}`}
            onClick={onSubmit}
            disabled={isSubmitDisabled}
            data-action-type={submitActionType}
          >
            {submitState === 'loading' && <span className="widget-btn-spinner" aria-hidden="true" />}
            {submitState === 'loading'
              ? 'Submitting...'
              : submitState === 'sent'
                ? 'Submitted'
                : (submit.label || 'Continue')}
          </button>
          {cancel && cancel.action && (
            <button
              type="button"
              className={`widget-btn widget-btn-cancel ${cancelState === 'loading' ? 'is-loading' : ''} ${cancelState === 'sent' ? 'is-done' : ''}`}
              onClick={onCancel}
              disabled={!conversationId || !pendingActionId || cancelState === 'loading' || cancelState === 'sent'}
              data-action-type={cancelActionType}
            >
              {cancelState === 'loading'
                ? 'Canceling...'
                : cancelState === 'sent'
                  ? 'Canceled'
                  : (cancel.label || 'Cancel')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default OrderPickerCard;
